import React from 'react';
import {
    Box,
    Chip,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Checkbox,
    ListItemText,
} from '@mui/material';

interface Item {
    id: string;
    name: string;
}

interface ItemSelectorProps {
    label: string;
    items: Item[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    helperText?: string;
    disabled?: boolean;
}

export default function ItemSelector({
    label,
    items,
    selectedIds,
    onChange,
    helperText = 'Leave empty to include all items',
    disabled = false,
}: ItemSelectorProps) {
    const handleChange = (event: SelectChangeEvent<typeof selectedIds>) => {
        const value = event.target.value;
        onChange(typeof value === 'string' ? value.split(',') : value);
    };

    return (
        <FormControl sx={{ width: '100%', mt: 1 }} disabled={disabled}>
            <InputLabel id={`${label.toLowerCase()}-select-label`}>{label}</InputLabel>
            <Select
                labelId={`${label.toLowerCase()}-select-label`}
                multiple
                value={selectedIds}
                onChange={handleChange}
                input={<OutlinedInput label={label} />}
                renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(value => {
                            const item = items.find(i => i.id === value);
                            return <Chip key={value} label={item?.name || value} />;
                        })}
                    </Box>
                )}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 224,
                        },
                    },
                }}
            >
                {items.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                        <Checkbox checked={selectedIds.includes(item.id)} />
                        <ListItemText primary={item.name} />
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
    );
}
